
import React, { useState, Suspense, lazy } from 'react';
import ImageDetailModal, { SmilePair } from './landing/SmileGallery';
import HeroSection from './landing/HeroSection';
import TrustedBySection from './landing/TrustedBySection';
import TransformationSection from './landing/TransformationSection';
import ComparisonSection from './landing/ComparisonSection';
import ValueProposition from './landing/ValueProposition';
import WorkflowSection from './landing/WorkflowSection';
import SEOHead from './seo/SEOHead';
import { generateHomepageSchemas } from '../utils/structuredData';

import BackgroundAnimation from './landing/BackgroundAnimation';

// Lazy load below-the-fold sections
const FeaturesSection = lazy(() => import('./landing/FeaturesSection'));
const UseCases = lazy(() => import('./landing/UseCases'));
const TestimonialsSection = lazy(() => import('./landing/TestimonialsSection'));
const FAQSection = lazy(() => import('./landing/FAQSection'));
const Footer = lazy(() => import('./landing/Footer'));

// --- FAQ DATA (for structured data schema) ---
const faqData = [
  {
    question: "How accurate are the AI simulations?",
    answer: "Our AI is trained on millions of clinical dental images and adheres to strict anatomical guidelines. While simulations are visual previews, they are designed to be clinically achievable and realistic."
  },
  {
    question: "Is patient data secure?",
    answer: "Absolutely. We are fully HIPAA compliant. All data is encrypted in transit and at rest, and we never share patient images with third parties without explicit consent."
  },
  {
    question: "Can I customize the smile designs?",
    answer: "Yes! You can choose from various style presets (Natural, Hollywood, Soft) and even use text prompts to refine specific details like 'close gap' or 'whiten teeth'."
  },
  {
    question: "Do I need special hardware?",
    answer: "No. FuturaSmile is cloud-based and works on any modern computer or tablet with an internet connection. No expensive graphics cards required."
  }
];

// --- DATA ---

// Hero Section Image Pairs - 5 before/after pairs using 1:1 ratio images
const smilePairsCol1: SmilePair[] = [
  { before: '/images/hero/1before.jpg', after: '/images/hero/1after.jpg' },
];

const smilePairsCol2: SmilePair[] = [
  { before: '/images/hero/2before.jpg', after: '/images/hero/2after.jpg' },
  { before: '/images/hero/3before.jpg?v=2', after: '/images/hero/3after.jpg?v=2' },
];

const smilePairsCol3: SmilePair[] = [
  { before: '/images/hero/4before.jpg', after: '/images/hero/4after.jpg' },
  { before: '/images/hero/5before.jpg', after: '/images/hero/5after.jpg' },
];

interface HomePageProps {
  onLaunchApp: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onLaunchApp }) => {
  const [selectedPair, setSelectedPair] = useState<SmilePair | null>(null);

  return (
    <>
      <SEOHead
        title="AI-Powered Dental Smile Simulation | Turn Consultations Into Commitments"
        description="The world's most advanced AI smile simulation platform. Generate photorealistic, clinically accurate treatment previews in 30 seconds. Trusted by 500+ dental practices."
        canonical="https://futurasmile.com"
        ogImage="https://futurasmile.com/images/og-image.jpg"
        structuredData={generateHomepageSchemas(faqData)}
      />
      <div className="relative text-zinc-100 font-sans selection:bg-teal-500/30 min-h-screen">
        {/* Background Animation */}
        <BackgroundAnimation />

        <HeroSection
          onLaunchApp={onLaunchApp}
          onImageClick={setSelectedPair}
          smilePairsCol1={smilePairsCol1}
          smilePairsCol2={smilePairsCol2}
          smilePairsCol3={smilePairsCol3}
        />

        <TrustedBySection />

        <TransformationSection onLaunchApp={onLaunchApp} />

        <ComparisonSection />

        <ValueProposition />

        <WorkflowSection />

        <Suspense fallback={<div className="py-32 text-center">Loading...</div>}>
          <FeaturesSection />
          <UseCases />
          <TestimonialsSection />
          <FAQSection />
          <Footer onLaunchApp={onLaunchApp} />
        </Suspense>

        {selectedPair && <ImageDetailModal pair={selectedPair} onClose={() => setSelectedPair(null)} />}
      </div>
    </>
  );
};

export default HomePage;
